import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import useNextPublicChallenge from '../../hooks/useNextPublicChallenge';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PlanSelector from '../../components/course/PlanSelector';
import PromoCountdown from '../../components/course/PromoCountdown';
import TuitionSchedule from '../../components/course/TuitionSchedule';
import CurriculumAccordion from '../../components/course/CurriculumAccordion';
import PreviewVideo from '../../components/course/PreviewVideo';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseHeroVisual from '../../components/course/CourseHeroVisual';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import {
  AI_TRADING_CHALLENGE_NAME,
  canonicalChallengeRunName,
} from '../challenges/challengePageUtils';
import {
  NO_CODING_BADGE, GUARANTEE_DAYS, COMPETITION, ROB_STORY,
  DELIVERABLES, SKILLS, REVIEW_SHOTS, VIDEO_REVIEWS,
} from '../../components/course/courseContent';
import './CoursePage.css';

const FIT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call';
export const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';
export const CHALLENGES_PATH = '/challenges';

/**
 * Education-page hero pattern (Harvard Online / Coursera / Maven style):
 * course name (H1) + one outcome headline + one short sub + CTA.
 * Outcome/sub are promise copy. Names, prices, dates, and challenge labels
 * come from the catalog / session groups.
 */
const HERO_OUTCOME = 'Place your first AI trade on day one. Finish with an AI agent that researches and trades for you every day.';
const HERO_SUB = 'Use AI to research, scan, trade, and run an AI trading agent with real money.';

/** The experiment changes the product name only. Everything else is shared. */
export const HERO_VARIANTS = {
  control: { key: 'trading', courseName: 'AI Trading Bootcamp' },
  agentic: { key: 'agentic', courseName: 'Agentic Trading Bootcamp' },
};

/**
 * Familiarity cards: title lives in the NanoBanana image. Below the image is
 * the problem, then a clear "How we fix this" solve. Keep language generic
 * (stocks, options, crypto, any session), not morning-only daytrading.
 */
const PAIN_POINTS = [
  {
    img: '/images/courses/problem-scan-time.webp',
    title: 'Research eats hours before you trade',
    problem: 'Tickers, news, levels, endless tabs. Traders on Reddit and X keep saying AI cut that grind from hours to minutes.',
    solve: 'We teach you a Claude, Codex, Cursor, or BotSpot scan workflow that builds a short, broker-checked list so research stops eating your day.',
  },
  {
    img: '/images/courses/problem-ai-wrong-price.webp',
    title: 'ChatGPT gave you a price. Your broker disagreed.',
    problem: 'The model sounds sure, then the number is stale or off by a few ticks. Close enough to trust. Wrong enough to hurt.',
    solve: 'We show you how to connect to your real broker so prices, positions, and account value come from the account you trade, not a chatbot guess.',
  },
  {
    img: '/images/courses/problem-luck-or-edge.webp',
    title: 'Your AI bot had a green week. Skill or luck?',
    problem: 'A short win streak is not proof. Traders keep asking the same thing: was that edge, or a fluke?',
    solve: 'We show you how to backtest and run evals on an AI trading agent so you can tell a lucky streak from a process that holds up.',
  },
  {
    img: '/images/courses/problem-chart-vs-broker.webp',
    title: 'TradingView is great for charts. Painful for trades.',
    problem: 'Broker connections drop, sessions log you out, and a lot of people still chart in one place and click the order somewhere else.',
    solve: 'We show you how to use Claude, Codex, Cursor, or BotSpot so chart work and a broker-ready trade live in the same agent workflow.',
  },
  {
    img: '/images/courses/problem-pine-locked.webp',
    title: 'AI wrote your Pine Script. You still do not trust it.',
    problem: 'Anyone can paste a prompt and get an indicator. The hard part is knowing what it measures, when it fails, and whether you would trade it with real money.',
    solve: 'We teach you to generate, stress-test, and use indicators with Claude, Codex, Cursor, or BotSpot so you ship something you understand.',
  },
  {
    img: '/images/courses/problem-scanner-homework.webp',
    title: 'Building a scanner feels like homework',
    problem: 'Platform script errors, outdated tutorials, and “too complex” walls show up the second you try to scan the market your way.',
    solve: 'You build scanners in plain English with Claude, Codex, Cursor, or BotSpot, then verify names against real market data instead of wrestling platform code.',
  },
  {
    img: '/images/courses/problem-options-legs.webp',
    title: 'An iron condor should not take ten clicks and a prayer',
    problem: 'Multi-leg options are tedious to build, fill, and manage in broker UIs. Traders talk about partial fills and babysitting every leg.',
    solve: 'You describe the structure in plain English, review the legs with AI, then send a checked order through your broker instead of clicking each leg by hand.',
  },
  {
    img: '/images/courses/problem-talk-to-tools.webp',
    title: 'You wish you could just tell Claude what to do',
    problem: 'Research, scan, review a trade, repeat. In plain English. Not another maze of menus, scripts, and disconnected screens.',
    solve: 'That is the core of the cohort: Claude, Codex, Cursor, BotSpot, and your broker working as one command seat you control.',
  },
];

const ABOUT_VISIBLE = [
  'This is a live five-week bootcamp taught by Rob Grzesik, CEO of Lumiwealth BotSpot. He helped build Voyager into a billion-dollar crypto company, and he built technology at Greystone, a mortgage lender, that handled over $3 billion in transactions. He also has a Master of Finance, 25+ years of coding experience, and 15+ years in financial technology.',
  'You are not watching lecture videos. Each week you show up on Zoom for a hands-on class, build the workflow live, place a reviewed trade, and leave with a recording you can replay forever. You also join the community: work with other students, retake future classes, and keep new material we add going forward.',
  'Week one gets your AI trading workspace working: Claude, Codex, Cursor, BotSpot, market data, and your broker. You place your first stock or ETF trade the same night.',
  'Week two turns AI into a research desk that saves hours. You pull filings, financials, earnings, insider data, and news, then build scanners that surface the next candidates. Stop spending hours building watchlists. Stop guessing when you skip the work.',
];

const ABOUT_MORE = [
  'Week three has two jobs. First, you learn Claude with TradingView to analyze charts and setups. Second, you learn to trade options, crypto, crypto futures, and prediction markets with AI doing the research and preparing the trades. Even if you are not an options trader yet, Claude, Codex, Cursor, or BotSpot can help you understand and build complex options trades.',
  'Week four turns a plain-English idea into a deterministic Python strategy you can read and trust. You backtest it, review the results, then iterate and improve. That is how you learn from the past before the same rules place trades without babysitting every click.',
  'Week five finishes with an AI trading agent you control. It can research, decide, and place trades on a schedule inside guardrails you set. When the cohort ends, that workspace keeps working for you.',
  'Plan on about one to two hours for each live Zoom call, plus about one to two hours of practice between sessions on your own setup with the prompts and checklists from class.',
  'You also get lifetime access, so you can retake later cohorts, keep new material we add, and join the optional five-week trading competition if you want a shared scoreboard. Real money is encouraged. The front door is using AI to research, review, and trade with control.',
];

const ABOUT_INFOGRAPHIC = {
  src: '/images/courses/about-bootcamp-infographic.webp',
  alt: 'Five-week AI Trading Bootcamp path: Week 1 first trade, Week 2 AI research, Week 3 charts and markets, Week 4 Python strategy, Week 5 AI trading agent',
};

/**
 * Challenge labels come from the live public session-group catalog, never from
 * a hard-coded month/year on this page. When the next run is renamed in the DB
 * (August → September → …), this string updates with it.
 */
const challengeDisplayName = (challenge) => {
  const raw = challenge?.name || challenge?.courseTitle || AI_TRADING_CHALLENGE_NAME;
  return canonicalChallengeRunName(raw);
};

const INCLUDED = [
  {
    img: '/images/courses/inc-live-sessions.webp',
    name: 'Five live sessions with Rob',
    detail: 'Build live each week, ask questions on camera, and bring your own setup for review.',
  },
  {
    img: '/images/courses/inc-lifetime.webp',
    name: 'Lifetime access',
    detail:
      'Join once and keep coming back. Retake later cohorts as many times as you want, '
      + 'and keep access to new material we add.',
  },
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'Recordings and course library',
    detail:
      'Every live session lands in your portal. Rewatch this cohort whenever you want, '
      + 'plus past classes and future sessions we add to the library.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'Prompts and workflow pack',
    detail:
      'The exact checklists, prompts, and working files used in class, so you can rerun the same workflow after the call ends.',
  },
  {
    img: '/images/courses/inc-leaderboard.webp',
    name: COMPETITION.name,
    detail: COMPETITION.stackDetail,
  },
  {
    img: '/images/courses/inc-guarantee.webp',
    name: `${GUARANTEE_DAYS}-day money-back guarantee`,
    detail: `Try the bootcamp. If it is not for you, request a refund within ${GUARANTEE_DAYS} days. No forms and no argument.`,
  },
];

const REQUIREMENTS = [
  {
    img: '/images/courses/need-computer.webp',
    title: 'A computer (recommended)',
    detail: 'Mac, Windows, or Linux. Phone works in a pinch. A laptop or desktop is much easier.',
  },
  {
    img: '/images/courses/need-time.webp',
    title: 'About three hours a week',
    detail: 'One live session plus time to practice on your own setup.',
  },
  {
    img: '/images/courses/need-plain-english.webp',
    title: 'No coding experience',
    detail: 'You direct Claude and Codex in plain English. We build the workspace together.',
  },
];

/** Maven/Brunson qualify pattern: turn the wrong buyer away on purpose. */
const FIT_YES = [
  'You want AI for real research, market scanning, trading, and automation.',
  'You want to place trades while you learn, not just watch lectures.',
  'You want an AI agent that can trade for you every day, under your control.',
  'You already fight tools like TradingView, thinkorswim, or a broker UI and want less friction.',
];

const FIT_NO = [
  'You want a get-rich-quick black box and guaranteed profits.',
  'You only want to watch videos and never place a trade.',
  'You want fully hands-off set-and-forget with zero reviews or guardrails.',
  'You cannot spare about three hours a week during the cohort.',
];

const NOT_COVERED = [
  'Signals you buy and follow blindly with no review.',
  'Managing your money for you. You stay in command.',
  'A coding bootcamp that starts with syntax instead of trading work.',
];

const FAQS = [
  {
    q: 'Do I need to know how to code?',
    a: 'No. You direct Claude and Codex in plain English, and we build the workspace together. Deeper coding paths are optional if you want them later.',
  },
  {
    q: 'What is agentic trading?',
    a: 'It means giving AI a trading job it can repeat: research the market, scan for a setup, prepare or place a reviewed order, and run again on a schedule inside rules you control.',
  },
  {
    q: 'Will I trade real money in class?',
    a: 'Yes. The bootcamp is built for real-money workflows with your broker under your control. Every trade is reviewed by you before it goes out. Real money is encouraged so the skills transfer to your real account.',
  },
  {
    q: 'Is this a course about building trading bots?',
    a: 'You start by using AI to research and place reviewed trades. By the end you have an AI agent that can research, decide, and trade every day inside guardrails you set.',
  },
  {
    q: 'Which markets and asset classes does the bootcamp cover?',
    a: 'Weeks one and two focus on stocks, ETFs, research, and scanners. Week three covers options, crypto and crypto futures with Bitunix, and prediction markets with Polymarket, using Claude with TradingView.',
  },
  {
    q: 'When do live sessions run?',
    a: 'Five Wednesday evenings at 7:00 PM Eastern. Each live session is about 1 to 2 hours. Plan on about 3 hours a week total once you include practice outside class. Exact dates are listed in the Schedule section. Every session is recorded.',
  },
  {
    q: 'What happens in a live session?',
    a: 'Rob demonstrates the workflow, you build alongside him, and the group reviews real setups and questions.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and added to your portal. You keep the library, including past classes and future sessions we add.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes. Join once and you can retake later live cohorts as many times as you want. New material stays in your course library.',
  },
  {
    q: 'What is the trading competition?',
    a: `${COMPETITION.body} ${COMPETITION.points.join(' ')}`,
  },
  {
    q: 'Can I try it before I enroll?',
    a: `Yes. Join the free ${AI_TRADING_CHALLENGE_NAME} first. Start one useful AI trading workflow at the live kickoff, keep improving that same project with light tips, and bring whatever you have to the Live Wrap-Up one week later. Dates come from the live challenge schedule.`,
  },
  {
    q: 'How does pricing work across the enrollment windows?',
    a: 'Pricing windows, dates, and amounts all come from the live promotion schedule for the current cohort. See the How pricing works section on this page for the windows that are active right now.',
  },
  {
    q: 'What if it is not for me?',
    a: `Request a refund within ${GUARANTEE_DAYS} days. No forms and no argument.`,
  },
];

const AboutSection = ({ challenge }) => {
  const [expanded, setExpanded] = useState(false);
  const challengeHref = challenge?.path || CHALLENGES_PATH;
  const challengeName = challengeDisplayName(challenge);
  return (
    <section className="cp-section cp-about" data-testid="about-bootcamp">
      <Reveal><h2>About this bootcamp</h2></Reveal>
      <div className="cp-about-layout">
        <Reveal delay={40} className="cp-about-visual">
          <img
            src={ABOUT_INFOGRAPHIC.src}
            alt={ABOUT_INFOGRAPHIC.alt}
            width="1280"
            height="714"
            loading="lazy"
          />
        </Reveal>
        <Reveal delay={80} className="cp-about-copy">
          <div className="cp-about-body">
            {ABOUT_VISIBLE.map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
            {expanded ? ABOUT_MORE.map((para) => <p key={para.slice(0, 40)}>{para}</p>) : null}
            {expanded ? (
              <p data-testid="about-free-challenge">
                Not ready to enroll yet? Join the free {challengeName} first.
                Kickoff is a live Zoom class to set up Claude, Codex, Cursor, and BotSpot.
                Keep improving that same project with light tips during the week, then bring
                whatever you have to the Live Wrap-Up for help and next steps.{' '}
                <Link
                  className="cp-inline-link"
                  to={challengeHref}
                  onClick={() => pushEvent({
                    event: ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED.name,
                    source: window.location.pathname,
                    path: 'free_challenge',
                    challenge_id: challenge?.id || null,
                    challenge_slug: challenge?.slug || null,
                    cta_location: 'about_bootcamp',
                  })}
                >
                  Join the free challenge
                </Link>
                .
              </p>
            ) : null}
            <button
              type="button"
              className="cp-show-more"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FreePreviewBand = ({ challenge, kickoffLabel, compact = false }) => {
  const href = challenge?.path || CHALLENGES_PATH;
  const cta = challenge && kickoffLabel
    ? `Join free live challenge · ${kickoffLabel}`
    : 'See free live challenges';

  return (
    <section
      className={`cp-section cp-challenge-path ${compact ? 'cp-challenge-path--compact' : ''}`.trim()}
      data-testid={compact ? 'challenge-path-secondary' : 'challenge-path'}
    >
      <Reveal>
        <p className="cp-challenge-kicker">Try before you buy</p>
        <h2>{compact ? 'Want a free live session first?' : `Join the free ${AI_TRADING_CHALLENGE_NAME}`}</h2>
      </Reveal>
      <Reveal delay={70}>
        <p>
          Before you pay for the five-week bootcamp, join the free {AI_TRADING_CHALLENGE_NAME}.
          Start one useful AI trading project at the live kickoff, keep improving that same
          project with light tips, and bring whatever you have to the Live Wrap-Up one week
          later. There are no daily assignments or required submissions. See how Rob teaches,
          then enroll in the full cohort if it fits.
        </p>
      </Reveal>
      {challenge && kickoffLabel ? (
        <Reveal delay={100}>
          <div className="cp-challenge-next" data-testid="next-challenge">
            <strong>{challengeDisplayName(challenge)}</strong>
            <span>Next live kickoff {kickoffLabel} · Eastern</span>
          </div>
        </Reveal>
      ) : (
        <Reveal delay={100}>
          <p className="cp-challenge-fallback">
            Upcoming free live challenges post here when the next kickoff is scheduled.
          </p>
        </Reveal>
      )}
      <Reveal delay={130}>
        <div className="cp-challenge-actions">
          <Link
            className="cp-btn-primary"
            to={href}
            onClick={() => pushEvent({
              event: ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED.name,
              source: window.location.pathname,
              path: 'free_challenge',
              challenge_id: challenge?.id || null,
              challenge_slug: challenge?.slug || null,
              cta_location: compact ? 'challenge_secondary' : 'challenge_primary',
            })}
          >
            {cta}
          </Link>
          {!challenge ? (
            <Link className="cp-btn-ghost" to={CHALLENGES_PATH}>See all free challenges</Link>
          ) : null}
        </div>
      </Reveal>
    </section>
  );
};

const ScheduleSection = ({ sessions, formatDate, cohortName }) => {
  if (!sessions.length) return null;
  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  const sessionTime = first?.scheduledAt
    ? new Date(first.scheduledAt).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
    })
    : '7:00 PM';

  return (
    <section className="cp-section cp-schedule" data-testid="schedule" id="schedule">
      <Reveal><h2>Schedule</h2></Reveal>
      <Reveal delay={40}>
        <p className="cp-schedule-lede">
          Syllabus above is what you build each week. This is when the live cohort runs.
        </p>
      </Reveal>
      <Reveal delay={70}>
        <div className="cp-schedule-card">
          {cohortName ? <p className="cp-schedule-cohort">{cohortName}</p> : null}

          <div className="cp-schedule-facts">
            <div>
              <span className="cp-schedule-kicker">Starts</span>
              <strong>{formatDate(first.scheduledAt)}</strong>
            </div>
            <div>
              <span className="cp-schedule-kicker">Ends</span>
              <strong>{formatDate(last.scheduledAt)}</strong>
            </div>
            <div>
              <span className="cp-schedule-kicker">Live time</span>
              <strong>Wednesdays · {sessionTime} Eastern</strong>
            </div>
          </div>

          <ul className="cp-schedule-notes">
            <li>
              <strong>{sessions.length} live sessions</strong>
              <span>1 to 2 hours on Zoom. Every session is recorded.</span>
            </li>
            <li>
              <strong>About 3 hours a week</strong>
              <span>Class plus practice time outside the live call.</span>
            </li>
            <li>
              <strong>Trading competition</strong>
              <span>The whole class competes for the best return over the five weeks, trading with AI.</span>
            </li>
          </ul>

          <ol className="cp-schedule-weeks">
            {sessions.map((session, index) => (
              <li key={session.id || index}>
                <span className="cp-schedule-week">Week {index + 1}</span>
                <span className="cp-schedule-day">{formatDate(session.scheduledAt)}</span>
              </li>
            ))}
          </ol>
        </div>
      </Reveal>
    </section>
  );
};

const CtaBand = ({ line, label, onEnroll, disabled }) => (
  <Reveal>
    <div className="cp-cta-band">
      <p>{line}</p>
      <button type="button" className="cp-btn-primary" data-testid="enroll-curriculum"
        onClick={onEnroll} disabled={disabled}>
        {label}
      </button>
    </div>
  </Reveal>
);

const CoursePage = ({ variantKey = 'control', experimentContext = null }) => {
  const { user } = useUser();
  const hero = HERO_VARIANTS[variantKey] || HERO_VARIANTS.control;
  const { startCheckout } = useCheckoutRedirect('course_sales');
  const {
    isLoading, plans, upfrontPlan, selfPaced, sessions, cohortStart, cohortName,
    isEnrollable, formatSessionDate, promoEndsAt, promoTierName,
    hasActivePromo, tuitionSchedule, pricingError,
  } = useCourseCatalog();
  const { challenge: nextChallenge, kickoffLabel: nextChallengeKickoff } = useNextPublicChallenge();
  const challengeName = challengeDisplayName(nextChallenge);

  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  useEffect(() => {
    if (!selectedPlanKey && upfrontPlan) setSelectedPlanKey(upfrontPlan.planKey);
  }, [upfrontPlan, selectedPlanKey]);

  const selectedPlan = plans.find((plan) => plan.planKey === selectedPlanKey) || upfrontPlan;

  const track = useCallback((event, extra = {}) => {
    pushEvent({
      event,
      source: window.location.pathname,
      variant: hero.key,
      userId: user?.id || user?.sub,
      email: user?.email,
      ...(experimentContext || {}),
      ...extra,
    });
  }, [experimentContext, hero.key, user]);

  const enroll = useCallback((ctaLocation) => {
    const planKey = selectedPlan?.planKey || 'bootcamp-upfront';
    track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, {
      cta_location: ctaLocation,
      path: 'live',
      productSlug: 'ai-bootcamp',
      planKey,
    });
    return startCheckout({
      products: [{ productSlug: 'ai-bootcamp', planKey }],
      flowContext: 'course_sales',
      metadata: {
        origin: {
          flow: 'course_sales',
          ctaLocation,
          variant: hero.key,
          experimentName: experimentContext?.experiment_name,
          experimentVariant: experimentContext?.experiment_variant,
          returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
        },
      },
      contact: { email: user?.email || '', name: user?.name || '' },
    });
  }, [startCheckout, track, selectedPlan, hero.key, experimentContext, user]);

  const startLabel = useMemo(
    () => (isEnrollable && cohortStart ? formatSessionDate(cohortStart) : null),
    [isEnrollable, cohortStart, formatSessionDate]
  );

  const facts = useMemo(() => ([
    {
      value: '5', label: 'Live sessions with Rob',
      card: '/images/courses/fact-card-sessions.webp',
      blurb: 'One session a week, taught live and recorded.',
    },
    {
      value: '3 hours', label: 'A week', card: '/images/courses/fact-card-hours.webp',
      blurb: 'The live session plus time to practise.',
    },
    {
      value: `${GUARANTEE_DAYS} days`, label: 'Money-back guarantee',
      card: '/images/courses/fact-card-guarantee.webp',
      blurb: `Request a refund within ${GUARANTEE_DAYS} days if it is not for you.`,
    },
  ]), []);

  // Never say "Enrollment unavailable" while catalog/sessions are still loading.
  // Slow connections would make people think the cohort is closed.
  const enrollmentUnavailable = Boolean(pricingError) || !isEnrollable;
  const ctaDisabled = isLoading || enrollmentUnavailable;
  const ctaLabel = enrollmentUnavailable && !isLoading
    ? 'Enrollment unavailable'
    : 'Enroll now';
  const challengeKickoffShort = useMemo(() => {
    if (!nextChallenge?.kickoffAt) return null;
    return new Date(nextChallenge.kickoffAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York',
    });
  }, [nextChallenge]);

  return (
    <div className="course-page" data-testid="course-page" data-variant={hero.key}>
      <ScrollProgress />
      <Helmet>
        <title>{`${hero.courseName} | BotSpot`}</title>
        <meta name="description" content={`${HERO_OUTCOME} ${HERO_SUB}`} />
        <link rel="canonical" href="https://botspot.trade/courses/ai-trading-bootcamp" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      {hasActivePromo && promoEndsAt && (
        <div className="cp-promobar" data-testid="promo-bar">
          <div className="cp-promobar-copy">
            <span className="cp-promobar-title">
              {promoTierName || 'Current pricing'}
              {upfrontPlan?.hasDiscount && upfrontPlan.originalDisplay && upfrontPlan.display
                ? (
                  <>
                    {' · '}
                    <s className="cp-promobar-was" data-testid="promo-was-price">
                      was {upfrontPlan.originalDisplay}
                    </s>
                    {', now '}
                    <span data-testid="promo-now-price">{upfrontPlan.display}</span>
                  </>
                )
                : null}
            </span>
            <PromoCountdown
              endsAt={promoEndsAt}
              label={`${promoTierName || 'Current pricing'} ends in`}
              variant="blocks"
            />
          </div>
        </div>
      )}

      <header className="cp-hero">
        {/* Decorative motion layer behind the hero: drifting grid, aurora
            orbs, rising particles. Pure CSS, no pointer events, hidden from
            assistive tech, stilled by prefers-reduced-motion. */}
        <div className="cp-hero-fx" aria-hidden="true">
          <div className="cp-hero-fx-grid" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--amber" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--blue" />
          <div className="cp-hero-fx-particles">
            {Array.from({ length: 10 }, (_, i) => (
              <span key={i} className={`cp-hero-fx-p cp-hero-fx-p--${i + 1}`} />
            ))}
          </div>
        </div>
        <div className="cp-hero-copy">
          <Reveal>
            <p className="cp-eyebrow" data-testid="hero-eyebrow">
              <span className="cp-live-dot" aria-hidden="true" />
              Live five-week cohort
              {startLabel ? <> · Starts {startLabel}</> : null}
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h1 data-testid="hero-course-name">{hero.courseName}</h1>
          </Reveal>
          <Reveal delay={100}>
            <p className="cp-hero-outcome" data-testid="hero-outcome">{HERO_OUTCOME}</p>
          </Reveal>
          <Reveal delay={140}>
            <p className="cp-sub" data-testid="hero-sub">{HERO_SUB}</p>
          </Reveal>
          <Reveal delay={180}><div className="cp-hero-rating"><StarRating rating={4.6} /></div></Reveal>

          <Reveal delay={220}>
            {pricingError ? (
              <p className="cp-price-error" data-testid="pricing-error" role="alert">
                Pricing is temporarily unavailable. Refresh or contact us for help.
              </p>
            ) : (
              <div className="cp-hero-price" data-testid="hero-price">
                <div className="cp-price-row">
                  {selectedPlan?.hasDiscount && selectedPlan.originalDisplay ? (
                    <s className="cp-price-was" data-testid="hero-was-price">
                      {selectedPlan.originalDisplay}
                    </s>
                  ) : null}
                  <strong className="cp-price-now" data-testid="hero-now-price">
                    {selectedPlan?.display || ''}
                    {selectedPlan?.installments > 1 ? <span className="cp-price-unit">/mo</span> : null}
                  </strong>
                  {selectedPlan?.savingsDisplay ? (
                    <span className="cp-price-save">Save {selectedPlan.savingsDisplay}</span>
                  ) : null}
                </div>
              </div>
            )}
          </Reveal>

          <Reveal delay={260}>
            <div className="cp-hero-cta">
              <button type="button" className="cp-btn-primary" data-testid="enroll-hero"
                onClick={() => enroll('hero')} disabled={ctaDisabled}
                aria-busy={isLoading || undefined}>
                <span className="cp-btn-label">{ctaLabel}</span>
              </button>
              <a
                className="cp-btn-ghost cp-btn-call"
                href={FIT_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'fit_call', cta_location: 'hero' })}
              >
                <span className="cp-btn-call-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                    <path
                      d="M7.2 3.8c.4-.4 1-.5 1.5-.3l2.2 1c.5.2.8.7.8 1.2v2.1c0 .4-.2.8-.5 1-.7.5-1 1.3-.7 2.1.6 1.7 2 3.1 3.7 3.7.8.3 1.6 0 2.1-.7.2-.3.6-.5 1-.5h2.1c.5 0 1 .3 1.2.8l1 2.2c.2.5.1 1.1-.3 1.5l-1.1 1.1c-.5.5-1.2.7-1.9.6-3.3-.6-6.3-2.4-8.5-4.6S4.4 10.4 3.8 7.1c-.1-.7.1-1.4.6-1.9l1.1-1.1Z"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="cp-btn-label">Talk to the Instructor</span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div className="cp-hero-free" data-testid="hero-free-challenge">
              <Link
                to={nextChallenge?.path || CHALLENGES_PATH}
                onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, {
                  path: 'free_challenge',
                  cta_location: 'hero',
                  challenge_id: nextChallenge?.id || null,
                })}
              >
                {nextChallenge
                  ? `Try ${challengeName} first`
                  : 'Try a free challenge first'}
              </Link>
              {challengeKickoffShort ? (
                <span className="cp-hero-free-date">Next kickoff {challengeKickoffShort}</span>
              ) : null}
            </div>
          </Reveal>

          {selfPaced && !enrollmentUnavailable ? (
            <Reveal delay={300}>
              <div className="cp-hero-free cp-hero-selfpaced" data-testid="hero-self-paced">
                <Link
                  to={SELF_PACED_PATH}
                  data-testid="hero-self-paced-link"
                  onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'self_paced', cta_location: 'hero' })}
                >
                  Can&apos;t make the live sessions? Go self-paced for {selfPaced.display}
                </Link>
              </div>
            </Reveal>
          ) : null}

          {enrollmentUnavailable && !isLoading ? (
            <Reveal delay={300}>
              <p className="cp-enroll-fallback" data-testid="enroll-fallback">
                Live dates are not open yet.{' '}
                {selfPaced ? <><Link to={SELF_PACED_PATH}>Start self-paced for {selfPaced.display}</Link>{', or '}</> : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">book a call with the instructor</a>.
              </p>
            </Reveal>
          ) : null}
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <CourseHeroVisual />
          <p className="cp-evidence-caption">
            See how a plain-language idea moves through review, approval, and monitoring.
          </p>
        </Reveal>
      </header>

      <LogoRow />
      <FactStrip facts={facts} />

      <section className="cp-section cp-problem" data-testid="pain-points">
        <Reveal><h2>Does this sound familiar?</h2></Reveal>
        <Reveal delay={40}>
          <p className="cp-problem-lede">
            We searched Reddit and X for the trading problems people complain about most.
            AI makes each of these much easier, and the cohort shows you how.
          </p>
        </Reveal>
        <div className="cp-problem-grid">
          {PAIN_POINTS.map((pain, index) => (
            <Reveal key={pain.title} delay={Math.min(index * 50, 280)}>
              <article className="cp-problem-card">
                <img
                  className="cp-problem-img"
                  src={pain.img}
                  alt={pain.title}
                  loading="lazy"
                  width="1280"
                  height="714"
                />
                <div className="cp-problem-copy">
                  <h3 className="cp-visually-hidden">{pain.title}</h3>
                  <p className="cp-problem-body">{pain.problem}</p>
                  <p className="cp-problem-fix-label">How we fix this</p>
                  <p className="cp-problem-solve">{pain.solve}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <p className="cp-turn">
            Problem, then practice: Claude, Codex, Cursor, BotSpot, and your broker working together,
            with real money under your control.
          </p>
        </Reveal>
      </section>

      <AboutSection challenge={nextChallenge} />

      <section className="cp-section cp-learn" data-testid="what-youll-learn">
        <Reveal><h2>What you'll learn</h2></Reveal>
        <div className="cp-deliverable-grid">
          {DELIVERABLES.map((item, index) => (
            <Reveal key={item.title} delay={index * 55}>
              <article className="cp-deliverable-card">
                <div className="cp-deliverable-media" aria-hidden="true">
                  <img
                    className="cp-deliverable-img"
                    src={item.img}
                    alt=""
                    width="1280"
                    height="800"
                    loading="lazy"
                  />
                </div>
                <div className="cp-deliverable-copy">
                  <span className="cp-deliverable-num" aria-hidden="true">{index + 1}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.detail}</p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={320}>
          <ul className="cp-skill-pills" aria-label="Course skills">
            {SKILLS.map((skill) => <li key={skill}>{skill}</li>)}
          </ul>
        </Reveal>
      </section>

      <PreviewVideo />

      {/* Maven/HBS pattern: founder-taught live courses put the instructor early. */}
      <section className="cp-section cp-instructor" aria-labelledby="instructor-heading">
        <Reveal>
          <h2 id="instructor-heading">{ROB_STORY.heading}</h2>
          <p className="cp-instructor-lede">Taught live by the founder. Not a hired instructor reading slides.</p>
        </Reveal>
        <Reveal delay={70}>
          <div className="cp-instructor-body">
            <aside className="cp-instructor-aside">
              <img src="/images/rob.jpg" alt="Rob Grzesik" width="220" height="220" decoding="async" />
              <ul className="cp-instructor-creds">
                {ROB_STORY.credentials.map((cred) => (
                  <li key={cred.label}>
                    <strong>{cred.value}</strong>
                    <span>{cred.label}</span>
                  </li>
                ))}
              </ul>
            </aside>
            <div className="cp-instructor-copy">
              <h3>Rob Grzesik</h3>
              <p className="cp-instructor-role">{ROB_STORY.roleLine}</p>
              {ROB_STORY.paragraphs.map((para) => (
                <p key={para.slice(0, 48)}>{para}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-included">
        <Reveal><h2>What's included</h2></Reveal>
        <ul className="cp-included-list">
          {INCLUDED.map((item, index) => (
            <Reveal as="li" key={item.name} delay={index * 55}>
              <img className="cp-included-img" src={item.img} alt="" loading="lazy" width="512" height="512" />
              <div><h3>{item.name}</h3><p>{item.detail}</p></div>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section cp-fit" data-testid="fit-section">
        <Reveal><h2>Who this is for</h2></Reveal>
        <Reveal delay={40}>
          <p className="cp-fit-lede">
            High-ticket cohorts work when the right people enroll.
            If this is you, keep going. If it is not, save your seat for someone else.
          </p>
        </Reveal>
        <div className="cp-fit-cols">
          <Reveal>
            <article className="cp-fit-col is-yes">
              <img className="cp-fit-banner" src="/images/courses/fit-good.webp" alt="" loading="lazy" width="1280" height="714" />
              <div className="cp-fit-col-body">
                <h3><span aria-hidden="true">✓</span> A good fit if</h3>
                <ul>{FIT_YES.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            </article>
          </Reveal>
          <Reveal delay={80}>
            <article className="cp-fit-col is-no">
              <img className="cp-fit-banner" src="/images/courses/fit-not.webp" alt="" loading="lazy" width="1280" height="714" />
              <div className="cp-fit-col-body">
                <h3><span aria-hidden="true">✕</span> Not a good fit if</h3>
                <ul>{FIT_NO.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            </article>
          </Reveal>
        </div>

        <div className="cp-need-block" data-testid="need-block">
          <Reveal delay={120}>
            <h3 className="cp-need-heading">What you need</h3>
          </Reveal>
          <ul className="cp-need-grid">
            {REQUIREMENTS.map((item, index) => (
              <Reveal as="li" key={item.title} delay={140 + index * 50}>
                <div className="cp-need-media">
                  <img
                    className="cp-need-img"
                    src={item.img}
                    alt=""
                    loading="lazy"
                    width="1024"
                    height="1024"
                  />
                </div>
                <div className="cp-need-copy">
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>

        <Reveal delay={280}>
          <div className="cp-not-covered">
            <h3>We will not cover</h3>
            <ul>
              {NOT_COVERED.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={300}>
          <p className="cp-fit-foot">
            Want to check the fit? <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Ask the instructor directly.</a>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-curriculum" id="curriculum" data-testid="curriculum">
        <Reveal><h2>Course syllabus</h2></Reveal>
        {sessions.length ? (
          <CurriculumAccordion
            sessions={sessions}
            formatDate={formatSessionDate}
            // The legacy duration field is the three-hour calendar reservation,
            // not the promised one-to-two-hour teaching time. Do not sell it as
            // instruction length on the public page.
            showDuration={false}
          />
        ) : (
          <Reveal>
            <p className="cp-muted" data-testid="curriculum-unavailable" role="status">
              Live dates and session details will appear here when the next cohort opens.
            </p>
          </Reveal>
        )}
      </section>

      <section className="cp-section cp-competition" data-testid="trading-competition">
        <Reveal><h2>{COMPETITION.heading}</h2></Reveal>
        <div className="cp-competition-layout">
          <div className="cp-competition-copy">
            <Reveal delay={40}>
              <p className="cp-competition-lede">{COMPETITION.body}</p>
            </Reveal>
            <Reveal delay={80}>
              <ul className="cp-competition-points">
                {COMPETITION.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={60} className="cp-competition-visual">
            <img
              className="cp-competition-img"
              src={COMPETITION.img}
              alt={COMPETITION.imgAlt}
              loading="lazy"
              width="1280"
              height="714"
            />
          </Reveal>
        </div>
      </section>

      <ScheduleSection
        sessions={sessions}
        formatDate={formatSessionDate}
        cohortName={cohortName}
      />

      <CtaBand line="Build it live, then keep every recording."
        label={ctaLabel} onEnroll={() => enroll('after_curriculum')}
        disabled={ctaDisabled} />

      <FreePreviewBand challenge={nextChallenge} kickoffLabel={nextChallengeKickoff} />

      <section className="cp-section cp-reviews" data-testid="student-reviews">
        <Reveal>
          <div className="cp-reviews-head"><h2>Student reviews</h2><StarRating rating={4.6} compact /></div>
        </Reveal>
        <Reveal delay={40}>
          <p className="cp-reviews-lede">Hear from students who took the course.</p>
        </Reveal>
        <div className="cp-video-reviews" data-testid="video-reviews">
          {VIDEO_REVIEWS.map((review, index) => (
            <Reveal key={review.youtubeId} delay={index * 50} className="cp-video-review">
              <div className="cp-video-frame">
                <iframe
                  title={review.title}
                  src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?rel=0`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="cp-video-caption">{review.name}</p>
            </Reveal>
          ))}
        </div>
        <div className="cp-reviews-wall">
          {REVIEW_SHOTS.map((shot, index) => (
            <Reveal key={shot.src} delay={index * 60} className="cp-review-shot">
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer"
                style={{ '--tilt': `${shot.tilt}deg` }}>
                <img src={shot.src} alt={shot.alt} loading="lazy" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={380}>
          <p className="cp-reviews-more"><a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">Read all reviews on Trustpilot</a></p>
        </Reveal>
      </section>

      <TuitionSchedule schedule={tuitionSchedule} cohortName={cohortName} />

      <section className="cp-section cp-pricing" id="pricing" data-testid="pricing">
        <Reveal><h2>Enrollment</h2></Reveal>
        {isLoading ? (
          <p className="cp-muted">Loading current pricing...</p>
        ) : pricingError ? (
          <Reveal>
            <div className="cp-pricing-card" data-testid="pricing-error-card">
              <p className="cp-price-error" role="alert">Current pricing could not be loaded. Refresh or contact the instructor.</p>
              <a className="cp-btn-primary" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Talk to the Instructor</a>
              <GuaranteeBlock compact />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={70}>
            <div className="cp-pricing-card">
              {hasActivePromo && promoEndsAt ? (
                <div className="cp-pricing-promo">
                  <p className="cp-pricing-promo-note">
                    {promoTierName || 'Current pricing'}
                    {upfrontPlan?.hasDiscount && upfrontPlan.originalDisplay
                      ? (
                        <>
                          {': '}
                          <s>{upfrontPlan.originalDisplay}</s>
                          {' '}
                          <strong>{upfrontPlan.display}</strong>
                          {' pay in full'}
                        </>
                      )
                      : null}
                  </p>
                  <PromoCountdown
                    endsAt={promoEndsAt}
                    label={`${promoTierName || 'Current pricing'} ends in`}
                    variant="blocks"
                  />
                </div>
              ) : null}
              <PlanSelector plans={plans} selectedKey={selectedPlanKey} onSelect={setSelectedPlanKey} />
              <button type="button" className="cp-btn-primary cp-btn-block" data-testid="enroll-pricing"
                onClick={() => enroll('pricing')} disabled={ctaDisabled}>{ctaLabel}</button>
              <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
              <GuaranteeBlock compact />
              <p className="cp-pricing-links">
                {selfPaced ? (
                  <Link
                    to={SELF_PACED_PATH}
                    data-testid="pricing-self-paced-link"
                    onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'self_paced', cta_location: 'pricing' })}
                  >
                    Self-paced from {selfPaced.display}
                  </Link>
                ) : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Book a call with the instructor</a>
              </p>
            </div>
          </Reveal>
        )}
      </section>

      <FreePreviewBand
        challenge={nextChallenge}
        kickoffLabel={nextChallengeKickoff}
        compact
      />

      <section className="cp-section cp-faq">
        <Reveal><h2>Frequently asked questions</h2></Reveal>
        <div className="cp-faq-list">
          {FAQS.map((faq) => (
            <Reveal key={faq.q}>
              <details><summary>{faq.q}</summary><p>{faq.a}</p></details>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="cp-section cp-final" style={{
        backgroundImage:
          'linear-gradient(rgba(6,11,22,0.72), rgba(6,11,22,0.88)), '
          + 'url("/images/courses/final-cta-bg.webp")',
      }}>
        <Reveal>
          <h2>Build an AI trading agent that keeps working</h2>
          <p>{startLabel ? `The next cohort starts ${startLabel}.` : 'Place a trade every week and finish with a scheduled agent.'}</p>
          <div className="cp-final-actions">
            <button type="button" className="cp-btn-primary" data-testid="enroll-final"
              onClick={() => enroll('final')} disabled={ctaDisabled}>
              <span className="cp-btn-label">{ctaLabel}</span>
            </button>
            <a className="cp-btn-ghost cp-btn-call" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">
              <span className="cp-btn-call-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                  <path
                    d="M7.2 3.8c.4-.4 1-.5 1.5-.3l2.2 1c.5.2.8.7.8 1.2v2.1c0 .4-.2.8-.5 1-.7.5-1 1.3-.7 2.1.6 1.7 2 3.1 3.7 3.7.8.3 1.6 0 2.1-.7.2-.3.6-.5 1-.5h2.1c.5 0 1 .3 1.2.8l1 2.2c.2.5.1 1.1-.3 1.5l-1.1 1.1c-.5.5-1.2.7-1.9.6-3.3-.6-6.3-2.4-8.5-4.6S4.4 10.4 3.8 7.1c-.1-.7.1-1.4.6-1.9l1.1-1.1Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cp-btn-label">Talk to the Instructor</span>
            </a>
          </div>
          <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
          {selfPaced ? (
            <p className="cp-final-selfpaced">
              Prefer to learn on your own schedule?{' '}
              <Link
                to={SELF_PACED_PATH}
                data-testid="final-self-paced-link"
                onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'self_paced', cta_location: 'final' })}
              >
                Get the self-paced version for {selfPaced.display}
              </Link>
            </p>
          ) : null}
        </Reveal>
      </section>

      <div className="cp-sticky">
        <span className="cp-sticky-meta">
          <span className="cp-sticky-course">{hero.courseName}</span>
          {startLabel ? <span className="cp-sticky-when">Starts {startLabel}</span> : null}
        </span>
        <span data-testid="sticky-price">
          {isLoading
            ? ''
            : (pricingError || !selectedPlan
              ? 'Pricing unavailable'
              : `${selectedPlan.display}${selectedPlan.installments > 1
                ? `/mo · ${selectedPlan.totalDisplay} total` : ''}`)}
        </span>
        <button type="button" className="cp-btn-primary" data-testid="enroll-sticky"
          onClick={() => enroll('sticky')} disabled={ctaDisabled}>
          <span className="cp-btn-label">{ctaLabel}</span>
        </button>
      </div>

      <CourseLegalDisclaimer />
    </div>
  );
};

export default CoursePage;
